import { take, call, put, race, delay } from 'redux-saga/effects';
import { eventChannel, buffers, END } from 'redux-saga';
import Raven from 'raven-js';

import { addMessage, syncMessages } from 'client/messages/_redux';
import { updateIssue } from 'client/support/_redux';
import { updatePayment } from 'client/payments/_redux';
import { getToken, logout } from 'auth';
import * as userActions from '../actions';

const WS_PROTOCOL = document.location.protocol === 'https:' ? 'wss' : 'ws';
const WS_URL = `${WS_PROTOCOL}://${document.location.host}/ws`;

const WS_RECONNECT_TIMEOUT = 5000;
const WS_IDENTITY_CHANGED = 4401;
const WS_INVALID_TOKEN = 4200;
const WS_TOKEN_EXPIRED = 4201;

const MESSAGE_TYPES = {
  connection_ready: syncMessages.request,
  new_notification: addMessage,
  user_update: userActions.profileUpdate,
  support_issue_update: updateIssue,
  payment_update: updatePayment,
};
const createWebsocketChannel = () => {
  let closeCode;
  const channel = eventChannel(emit => {
    const socket = new WebSocket(WS_URL);

    socket.onopen = () => {
      socket.send(getToken());
    };

    socket.onmessage = event => {
      const data = JSON.parse(event.data);

      const action = MESSAGE_TYPES[data.type];
      if (action) {
        emit(action(data.payload));
      } else {
        Raven.captureMessage(`Unused websocket: ${event}`);
      }
    };

    socket.onclose = event => {
      closeCode = event.code;
      if (event.code >= 4000 && event.code < 5000 && event.code !== WS_IDENTITY_CHANGED) {
        Raven.captureMessage(`Websocket closed with strange status: ${event.code}`);
      }
      emit(END);
    };

    return () => {
      socket.close();
    };
  }, buffers.expanding());
  channel.closeCode = () => closeCode;
  return channel;
};

function* socketChannelSaga(socketChannel) {
  while (true) {
    const action = yield take(socketChannel);
    yield put(action);
  }
}

function* websocketWatcherSaga() {
  while (true) {
    yield take(userActions.START_WS);

    while (true) {
      const socketChannel = yield call(createWebsocketChannel);

      const { stop } = yield race({
        channel: call(socketChannelSaga, socketChannel),
        stop: take(userActions.STOP_WS),
      });

      socketChannel.close();

      if (stop) {
        break;
      }

      if ([WS_INVALID_TOKEN, WS_TOKEN_EXPIRED].includes(socketChannel.closeCode())) {
        yield put(logout());
        break;
      }

      yield delay(socketChannel.closeCode() === WS_IDENTITY_CHANGED ? 0 : WS_RECONNECT_TIMEOUT);
      // connectType = RECONNECT_TYPE
    }
  }
}

export default websocketWatcherSaga;
